import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';

/**
 * A sponsored slot on the website. Other extensions use it via app.vouchaAds.Slot:
 *   m(app.vouchaAds.Slot, { placement: 'calculator', tool: 'tax', country: 'US' })
 *   m(app.vouchaAds.Slot, { placement: 'country_branding', tool: 'tax', country: 'US', label: 'US tax results' })
 * Renders nothing when no paid, approved booking matches. An impression is
 * counted once the ad has been at least half visible for a second.
 */
/**
 * Rotation memory, kept only in this browser for today: how many times each ad has been
 * shown, and the last ad in each space. Used to ask the server for variety.
 */
const SEEN_KEY = 'voucha-ads-seen';
function seenStore() {
  const day = new Date().toISOString().slice(0, 10);
  try {
    const s = JSON.parse(localStorage.getItem(SEEN_KEY) || '{}');
    if (s.day === day) return s;
  } catch (e) {}
  return {
    day,
    counts: {},
    last: {},
    cap: 6
  };
}

/** Ads to skip in this space: the one shown last, and any at today's cap. */
export function excludeFor(placement) {
  const s = seenStore();
  const ids = Object.keys(s.counts).filter(id => s.counts[id] >= (s.cap || 6));
  if (s.last[placement]) ids.push(String(s.last[placement]));
  return Array.from(new Set(ids)).join(',');
}
export function noteShown(placement, ad, cap) {
  if (!ad || !ad.id) return;
  const s = seenStore();
  s.counts[ad.id] = (s.counts[ad.id] || 0) + 1;
  s.last[placement] = ad.id;
  if (cap) s.cap = cap;
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(s));
  } catch (e) {}
}

/** The viewer's country from their time zone (no permission or personal data needed); '' if unclear */
export function viewerCountry() {
  let tz = '';
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (e) {}
  if (tz === 'Africa/Lagos') return 'NG';
  if (/^Europe\/(London|Belfast)$/.test(tz)) return 'GB';
  if (/^Europe\/Dublin$/.test(tz)) return 'IE';
  if (/^America\/(Toronto|Vancouver|Edmonton|Winnipeg|Halifax|Regina|St_Johns|Montreal|Moncton|Whitehorse|Yellowknife|Iqaluit)$/.test(tz)) return 'CA';
  if (/^America\/(New_York|Chicago|Denver|Los_Angeles|Phoenix|Anchorage|Detroit|Boise|Indiana\/.+|Kentucky\/.+)$/.test(tz) || /^Pacific\/Honolulu$/.test(tz)) return 'US';
  if (/^Asia\/Dubai$/.test(tz)) return 'AE';
  if (/^Africa\/(Johannesburg)$/.test(tz)) return 'ZA';
  if (/^Africa\/Accra$/.test(tz)) return 'GH';
  return '';
}
export function slotUrl(params) {
  const q = new URLSearchParams({
    tool: '',
    country: '',
    tag: '',
    vc: viewerCountry(),
    ...params,
    exclude: excludeFor(params.placement)
  });
  return app.forum.attribute('apiUrl') + '/voucha-ads/slot?' + q.toString();
}
export default class SponsorSlot extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.ad = null;
    this.key = null;
    this.fetch();
  }
  onupdate() {
    // Re-pick if the calculator's country or tool changed
    if (this.contextKey() !== this.key) this.fetch();
  }
  contextKey() {
    return [this.attrs.placement, this.attrs.tool || '', this.attrs.country || '', this.attrs.tag || ''].join('|');
  }
  fetch() {
    this.key = this.contextKey();
    this.ad = null;
    this.seen = false;
    const placement = this.attrs.placement;
    const key = this.key;
    app.request({
      method: 'GET',
      url: slotUrl({
        placement,
        tool: this.attrs.tool || '',
        country: this.attrs.country || '',
        tag: this.attrs.tag || ''
      }),
      errorHandler: () => {}
    }).then(r => {
      if (key !== this.key) return;
      this.ad = r.ad;
      noteShown(placement, r.ad, r.cap);
    }).catch(() => {}).finally(() => {
      m.redraw();
      // The slot may already be on screen: re-observe so visibility is checked now the ad is here
      if (this.observer && this.dom) {
        this.observer.unobserve(this.dom);
        this.observer.observe(this.dom);
      }
    });
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.dom = vnode.dom;
    this.watch(vnode.dom);
  }
  onremove() {
    this.observer?.disconnect();
    clearTimeout(this.timer);
  }
  watch(dom) {
    if (!('IntersectionObserver' in window) || !dom) return;
    this.observer = new IntersectionObserver(entries => {
      const visible = entries.some(e => e.intersectionRatio >= 0.5);
      clearTimeout(this.timer);
      if (visible && this.ad && !this.seen) {
        this.timer = setTimeout(() => this.countImpression(), 1000);
      }
    }, {
      threshold: [0, 0.5, 1]
    });
    this.observer.observe(dom);
  }
  countImpression() {
    if (this.seen || !this.ad) return;
    this.seen = true;
    app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/voucha-ads/impression',
      body: {
        token: this.ad.token
      },
      errorHandler: () => {}
    }).catch(() => {});
  }
  view() {
    if (!this.ad) return m("div", {
      className: "VouchaSlot is-empty"
    });
    return renderAd(this.ad, {
      compact: this.attrs.compact,
      label: this.attrs.label
    });
  }
}

/** The ad markup, shared by the slot component and plain (cached) rendering in the discussion list. */
export function renderAd(ad, opts = {}) {
  const label = ad.house ? 'From Voucha' : 'Sponsored';

  // House ad: moderator recruitment, generated for the tag being viewed
  if (ad.template === 'moderator') {
    const external = /^https?:|^mailto:/.test(ad.ctaUrl);
    return m("div", {
      className: 'VouchaAd VouchaAd--' + ad.placement + ' VouchaAd--house'
    }, m("div", {
      className: "VouchaAd-label"
    }, label), m("a", {
      href: ad.ctaUrl,
      target: external ? '_blank' : undefined,
      rel: "noopener",
      className: "VouchaMod"
    }, m("i", {
      className: "fas fa-user-shield VouchaMod-icon",
      "aria-hidden": "true"
    }), m("span", {
      className: "VouchaMod-kicker"
    }, "Voucha is looking for moderators"), m("strong", {
      className: "VouchaMod-title"
    }, "Help run ", ad.tagName), m("span", {
      className: "VouchaMod-text"
    }, "Keep the ", ad.tagName, " community useful, accurate and friendly. A few minutes a day."), m("span", {
      className: "VouchaMod-button"
    }, "Apply")));
  }

  // In-page banner: a wide image for computers and tablets, its own image for phones (the browser picks)
  if (ad.placement === 'page_banner' && ad.imageUrl) {
    return m("div", {
      className: 'VouchaAd VouchaAd--page_banner' + (ad.house ? ' VouchaAd--house' : '')
    }, m("div", {
      className: "VouchaAd-label"
    }, label), m("a", {
      href: ad.ctaUrl,
      target: "_blank",
      rel: "sponsored noopener",
      className: "VouchaAd-link"
    }, m("picture", null, ad.mobileImageUrl ? m("source", {
      media: "(max-width: 767px)",
      srcset: ad.mobileImageUrl
    }) : null, m("img", {
      src: ad.imageUrl,
      alt: ad.headline || 'Advertisement',
      loading: "lazy"
    }))));
  }

  // Website placements are image-only display ads (like Blind): the image is the ad, one link,
  // a small "Sponsored" label for disclosure, no company name, headline, text or button.
  if (['forum_feed', 'banner', 'calculator'].includes(ad.placement) && ad.imageUrl) {
    return m("div", {
      className: 'VouchaAd VouchaAd--' + ad.placement + (ad.house ? ' VouchaAd--house' : '')
    }, m("div", {
      className: "VouchaAd-label"
    }, label), m("a", {
      href: ad.ctaUrl,
      target: "_blank",
      rel: "sponsored noopener",
      className: "VouchaAd-link"
    }, m("img", {
      src: ad.imageUrl,
      alt: ad.headline || 'Advertisement',
      loading: "lazy"
    })));
  }
  if (ad.placement === 'country_branding') {
    return m("div", {
      className: "VouchaSlot VouchaSlot--brand"
    }, m("span", {
      className: "VouchaSlot-brandLabel"
    }, opts.label || 'Results', " powered by"), m("a", {
      href: ad.ctaUrl,
      target: "_blank",
      rel: "sponsored noopener",
      className: "VouchaSlot-brandLink"
    }, ad.logoUrl ? m("img", {
      src: ad.logoUrl,
      alt: ad.sponsorName
    }) : m("strong", null, ad.sponsorName)), ad.headline ? m("a", {
      href: ad.ctaUrl,
      target: "_blank",
      rel: "sponsored noopener",
      className: "VouchaSlot-brandMsg"
    }, ad.headline) : null, m("span", {
      className: "VouchaSlot-tag"
    }, "Sponsored"));
  }

  // Banner: image only
  if (!ad.headline && ad.imageUrl) {
    return m("div", {
      className: "VouchaSlot VouchaSlot--banner"
    }, m("div", {
      className: "VouchaSlot-label"
    }, "Advertisement"), m("a", {
      href: ad.ctaUrl,
      target: "_blank",
      rel: "sponsored noopener"
    }, m("img", {
      src: ad.imageUrl,
      alt: ad.sponsorName
    })));
  }
  return m("div", {
    className: 'VouchaSlot VouchaSlot--story' + (opts.compact ? ' VouchaSlot--compact' : '')
  }, m("div", {
    className: "VouchaSlot-inner"
  }, ad.imageUrl ? m("a", {
    href: ad.ctaUrl,
    target: "_blank",
    rel: "sponsored noopener",
    className: "VouchaSlot-imageLink"
  }, m("img", {
    src: ad.imageUrl,
    alt: ""
  })) : null, m("div", {
    className: "VouchaSlot-copy"
  }, m("div", {
    className: "VouchaSlot-meta"
  }, m("span", {
    className: "VouchaSlot-tag"
  }, "Sponsored"), " \xB7 ", ad.sponsorName, ad.foundingPartner ? ' · Founding Partner' : ''), m("a", {
    href: ad.ctaUrl,
    target: "_blank",
    rel: "sponsored noopener",
    className: "VouchaSlot-headline"
  }, ad.headline), String(ad.body || '').split(/\n\s*\n/).filter(p => p.trim()).map(p => m("p", null, p)), m("a", {
    href: ad.ctaUrl,
    target: "_blank",
    rel: "sponsored noopener",
    className: "Button Button--primary VouchaSlot-cta"
  }, ad.ctaLabel))));
}

/** Count an impression once the element has been at least half visible for a second. */
export function watchImpression(dom, ad) {
  if (!dom || !ad || !('IntersectionObserver' in window) || dom._vouchaToken === ad.token) return;
  dom._vouchaToken = ad.token;
  dom._vouchaObserver?.disconnect();
  let timer;
  let counted = false;
  const observer = new IntersectionObserver(entries => {
    clearTimeout(timer);
    if (!counted && entries.some(e => e.intersectionRatio >= 0.5)) {
      timer = setTimeout(() => {
        counted = true;
        observer.disconnect();
        app.request({
          method: 'POST',
          url: app.forum.attribute('apiUrl') + '/voucha-ads/impression',
          body: {
            token: ad.token
          },
          errorHandler: () => {}
        }).catch(() => {});
      }, 1000);
    }
  }, {
    threshold: [0, 0.5, 1]
  });
  observer.observe(dom);
  dom._vouchaObserver = observer;
}

/**
 * Fill a plain DOM element with an ad (for parts of the page built outside Mithril, e.g. the
 * theme's sidebar): fetch once, draw with m.render, track the impression. Draws nothing if
 * there's no ad, and does nothing if the element has been removed meanwhile.
 */
/**
 * avoid: ad ids this slot must not show (another slot on the page), with no repeat fallback: if nothing else
 * qualifies, `empty` runs. onAd(ad|null): told what was shown, e.g. to fill a second slot after this one.
 */
export function fillSlot(el, {
  placement,
  tag = '',
  tool = '',
  country = '',
  empty = null,
  avoid = null,
  onAd = null
} = {}) {
  if (!el || !app.forum) return;
  const token = String(Date.now()) + Math.random();
  el._vouchaFill = token;
  m.render(el, null);
  // empty(el): called when no ad is booked for this slot (e.g. to show an "Advertise here" card)
  const none = () => {
    if (empty && el.isConnected && el._vouchaFill === token) empty(el);
    if (onAd) onAd(null);
  };
  const extra = avoid && avoid.length ? {
    avoid: avoid.join(','),
    strict: '1'
  } : {};
  app.request({
    method: 'GET',
    url: slotUrl({
      placement,
      tag,
      tool,
      country,
      ...extra
    }),
    errorHandler: () => {}
  }).then(r => {
    if (!r || !r.ad) return none();
    if (!el.isConnected || el._vouchaFill !== token) return;
    noteShown(placement, r.ad, r.cap);
    m.render(el, renderAd(r.ad, {
      compact: true
    }));
    watchImpression(el.firstElementChild, r.ad);
    if (onAd) onAd(r.ad);
  }).catch(none);
}
flarum.reg.add('voucha-ads', 'forum/components/SponsorSlot', SponsorSlot);